import {
  AvailableTimeSlotsRequestPayload,
  BookedAppointment,
  KEY,
  TimeSlot,
  Slots
} from 'store/booking/types';
import { dfdAccessTokenSelector } from 'store/authentication/selectors';
import { apiKeySelector } from 'store/appSettings/selectors';
import axios from 'axios';
import Config from 'react-native-config';
import Logger, { LoggingModule } from 'services/Logger';
import SSEService from 'services/ServerSentEvents/SSEService';
import { SseListeners } from 'services/ServerSentEvents';
import { RootState } from 'store/types';
import { TRIGGER_SSE_UPDATES } from 'services/ServerSentEvents/SSEService.common';
import {
  currentCernerPersonIdSelector,
  currentPatientIdSelector,
  currentResourceIdProviderSelector,
  isLocationSlotsLoadedSelector,
  npv2SelectedProviderLocationSlotsSelector
} from '../selectors';

const buildBookingRequestParams = (
  state: RootState,
  upcomingAppointment?: BookedAppointment
): AvailableTimeSlotsRequestPayload => {
  const { appointmentDetails } = state.booking.appointment;
  const resourceId =
    upcomingAppointment?.cernerResourceId ||
    currentResourceIdProviderSelector(state)?.toString() ||
    '';

  if (upcomingAppointment) {
    const synonymId = upcomingAppointment?.appointmentReasonId || '';
    const visitTypeId = upcomingAppointment?.visitTypeId || '';
    const consumerId = upcomingAppointment?.consumerId || '';
    const providerId = upcomingAppointment?.providerPersonnelId.toString() || '';
    const locationId = upcomingAppointment?.appointmentLocation.locationId || '';
    return {
      visitTypeId,
      consumerId,
      locationId,
      synonymId,
      cernerPersonnelId: providerId,
      retrieveToken: true,
      resourceId
    };
  }
  const appointmentType = appointmentDetails?.appointmentType;
  let synonymId = '';
  let visitTypeId = '';

  if (appointmentType) {
    const visitTypes = appointmentDetails?.groupedVisitType?.visitTypes || [];
    // @ts-ignore
    const visitType = visitTypes.find(type => type.appointmentType === appointmentType);

    if (visitType) {
      synonymId = visitType?.synonymId;
      visitTypeId = visitType?.synonymId;
    }
  }
  const consumerId = appointmentDetails?.patient?.consumerId || '';
  const providerId = appointmentDetails?.doctor?.cernerPersonnelId.toString() || '';
  const locationId = appointmentDetails?.appointmentLocation?.locationId || '';
  return {
    visitTypeId,
    consumerId,
    locationId,
    synonymId,
    cernerPersonnelId: providerId,
    resourceId,
    retrieveToken: true
  };
};

/**
 * Get Time Slots
 */
export const GET_LOCATION_TIME_SLOTS = `${KEY}.GET_LOCATION_TIME_SLOTS`;
export const GET_LOCATION_TIME_SLOTS_FAIL = `${KEY}.GET_LOCATION_TIME_SLOTS_FAIL`;
export const GET_LOCATION_TIME_SLOTS_SUCCESS = `${KEY}.GET_LOCATION_TIME_SLOTS_SUCCESS`;
export const SET_INITIAL_TIME_SLOTS = `${KEY}.SET_INITIAL_TIME_SLOTS`;

export const GET_SLOTS_TOKEN = `${KEY}.GET_SLOTS_TOKEN`;
export const GET_SLOTS_TOKEN_SUCCESS = `${KEY}.GET_SLOTS_TOKEN_SUCCESS`;
export const GET_SLOTS_TOKEN_FAIL = `${KEY}.GET_SLOTS_TOKEN_FAIL`;
export const CLEAR_LOCATION_TIME_SLOTS = 'CLEAR_LOCATION_TIME_SLOTS';

// NPV2 flow actions
export const SET_SLOTS_PROVIDER_LOCATION_IDS = `${KEY}.SET_SLOTS_PROVIDER_LOCATION_IDS`;
export const SET_NPV2_PROVIDER_LOCATION_ID = `${KEY}.SET_NPV2_PROVIDER_LOCATION_ID`;
export const GET_NPV2_LOCATION_TIME_SLOTS_SUCCESS = `${KEY}.GET_NPV2_LOCATION_TIME_SLOTS_SUCCESS`;
export const SET_SELECTED_PROVIDER_LOCATION_SLOTS = `${KEY}.SET_SELECTED_PROVIDER_LOCATION_SLOTS`;
export const CLEAR_NPV2_STATE = `${KEY}.CLEAR_NPV2_STATE`;

export const getLocationTimeSlots = (upcomingAppointment?: BookedAppointment) => async (
  dispatch: Function,
  getState: Function
) => {
  dispatch({ type: GET_LOCATION_TIME_SLOTS });

  const state = getState();
  const patientId = upcomingAppointment?.patientId || currentCernerPersonIdSelector(state);
  const pingAccessToken = dfdAccessTokenSelector(state);
  const consumerId = upcomingAppointment?.consumerId || currentPatientIdSelector(state);
  const apikey = apiKeySelector(state);
  dispatch({ type: GET_SLOTS_TOKEN });

  const axiosInstance = axios.create({
    baseURL: `${Config.SCHEDULING_BASE_URL}`,
    headers: {
      Authorization: `Bearer ${pingAccessToken}`,
      'x-dfd-consumer-id': consumerId,
      'x-patient-id': patientId
    }
  });
  try {
    const apiKeyHeader: Record<string, any> = {};
    if (process.env.BUILD_ENV !== 'PROD' && Config.SCHEDULING_BASE_URL.includes('localhost')) {
      apiKeyHeader['x-api-key'] = apikey;
    } else {
      apiKeyHeader.apiKey = apikey;
    }
    // Currently we get the parameters for the connection from the mhp-scheduling-service below.
    // This likely will change in the future if we support multiple listener types, not just slots.
    // That will enable us to have a single connection to the hub and add more custom listeners
    // If the above does happen, this likely should be moved into the SSEService.ts or elsewhere
    const response = await axiosInstance.get('/slots/sse-jwt', {
      headers: apiKeyHeader
    });
    const { accessToken, topicUri } = response?.data;

    if (accessToken && topicUri) {
      dispatch({ type: GET_SLOTS_TOKEN_SUCCESS });
      await SSEService.initConnection(topicUri, accessToken);
      SSEService.addCustomListener(SseListeners.SLOTS, dispatch);
      // The Bull queue for the user is paused, while we establish the SSE connection.
      // This call will unpause it.
      const sseRequestData = buildBookingRequestParams(state, upcomingAppointment);
      await axiosInstance.post('/slots/sse', sseRequestData, {
        headers: apiKeyHeader
      });
      dispatch({ type: TRIGGER_SSE_UPDATES });
    } else {
      dispatch({ type: GET_SLOTS_TOKEN_FAIL });
      return;
    }
  } catch (e) {
    console.error(e);
    dispatch({ type: GET_SLOTS_TOKEN_FAIL });
  }
};

/**
 * Initializing the SSE connection.
 * @returns
 */
export const initializeSSE = () => async (dispatch: Function, getState: Function) => {
  dispatch({ type: GET_LOCATION_TIME_SLOTS });

  const state = getState();
  const patientId = currentCernerPersonIdSelector(state);
  const pingAccessToken = dfdAccessTokenSelector(state);
  const consumerId = currentPatientIdSelector(state);
  const apikey = apiKeySelector(state);
  dispatch({ type: GET_SLOTS_TOKEN });

  const axiosInstance = axios.create({
    baseURL: `${Config.SCHEDULING_BASE_URL}`,
    headers: {
      Authorization: `Bearer ${pingAccessToken}`,
      'x-dfd-consumer-id': consumerId,
      'x-patient-id': patientId
    }
  });
  try {
    const apiKeyHeader: Record<string, any> = {};
    if (process.env.BUILD_ENV !== 'PROD' && Config.SCHEDULING_BASE_URL.includes('localhost')) {
      apiKeyHeader['x-api-key'] = apikey;
    } else {
      apiKeyHeader.apiKey = apikey;
    }
    // Currently we get the parameters for the connection from the mhp-scheduling-service below.
    // This likely will change in the future if we support multiple listener types, not just slots.
    // That will enable us to have a single connection to the hub and add more custom listeners
    // If the above does happen, this likely should be moved into the SSEService.ts or elsewhere
    const response = await axiosInstance.get('/slots/sse-jwt', {
      headers: apiKeyHeader
    });
    const { accessToken, topicUri } = response?.data;

    if (accessToken && topicUri) {
      dispatch({ type: GET_SLOTS_TOKEN_SUCCESS });
      await SSEService.initConnection(topicUri, accessToken);
      SSEService.addCustomNPV2Listener(SseListeners.SLOTS, dispatch);
    } else {
      dispatch({ type: GET_SLOTS_TOKEN_FAIL });
      return;
    }
  } catch (e) {
    console.error('Failed to initialize SSE', e);
    dispatch({ type: GET_SLOTS_TOKEN_FAIL });
  }
};

/**
 * Seperating logic from SSE initialization, this will only make a request to get slots.
 * @returns
 */
export const getLocationTimeSlotsForNPV2 = () => async (dispatch: Function, getState: Function) => {
  dispatch({ type: GET_LOCATION_TIME_SLOTS });
  const state = getState();
  const patientId = currentCernerPersonIdSelector(state);
  const pingAccessToken = dfdAccessTokenSelector(state);
  const consumerId = currentPatientIdSelector(state);

  const axiosInstance = axios.create({
    baseURL: `${Config.SCHEDULING_BASE_URL}`,
    headers: {
      Authorization: `Bearer ${pingAccessToken}`,
      'x-dfd-consumer-id': consumerId,
      'x-patient-id': patientId
    }
  });
  const apikey = apiKeySelector(state);
  try {
    const apiKeyHeader: Record<string, any> = {};
    if (process.env.BUILD_ENV !== 'PROD' && Config.SCHEDULING_BASE_URL.includes('localhost')) {
      apiKeyHeader['x-api-key'] = apikey;
    } else {
      apiKeyHeader.apiKey = apikey;
    }
    const sseRequestData = buildBookingRequestParams(state);
    const { cernerPersonnelId, locationId, synonymId } = sseRequestData;
    const uniqueNpv2Id = `${cernerPersonnelId}-${locationId}-${synonymId}`;
    dispatch({ type: SET_NPV2_PROVIDER_LOCATION_ID, payload: uniqueNpv2Id });

    // Managing the location slots by adding to the timeSlots store
    if (!isLocationSlotsLoadedSelector(state)) {
      // The Bull queue for the user is paused, while we establish the SSE connection.
      // This call will unpause it.
      await axiosInstance.post('/slots/sse', sseRequestData, {
        headers: apiKeyHeader
      });
      dispatch({ type: SET_SLOTS_PROVIDER_LOCATION_IDS, payload: uniqueNpv2Id });
      dispatch({ type: TRIGGER_SSE_UPDATES });
    } else {
      const selectedProviderSlots = npv2SelectedProviderLocationSlotsSelector(state);
      // Updating timeSlots store to populate on Calendar
      dispatch({ type: GET_LOCATION_TIME_SLOTS_SUCCESS, payload: selectedProviderSlots });
    }
  } catch (e) {
    console.error(e);
    dispatch({ type: GET_LOCATION_TIME_SLOTS_FAIL, error: e });
  }
};

export const clearNpv2State = () => ({
  type: CLEAR_NPV2_STATE
});

export const setLocationIdsWithSlots = (locationId: string) => ({
  type: SET_SLOTS_PROVIDER_LOCATION_IDS,
  payload: locationId
});

export const setInitialTimeSlots = (slots: (TimeSlot | Slots)[]) => ({
  type: SET_INITIAL_TIME_SLOTS,
  payload: slots
});

export const closeTimeSlotListener = () => {
  try {
    SSEService.closeConnection();
  } catch (e) {
    Logger.error(`${LoggingModule.booking} failed to close slots connection, error: ${e}`);
  }
};

export const clearLocationTimeSlots = () => ({
  type: CLEAR_LOCATION_TIME_SLOTS
});
